// O portão não devia esperar a rede para saber quais portas existem.
//
// `/v1/auth/config` responde em cerca de 100 ms de um desktop com fibra, e
// nesse intervalo o portão desenha o título e nenhum botão — depois eles
// aparecem de repente. Numa rede de telemóvel esse intervalo é bem maior e
// a página parece partida.
//
// A resposta quase nunca muda: é o que o servidor tem configurado. Então
// guardamos a última e começamos por ela, trocando pela verdadeira quando
// chegar. Quem já entrou aqui uma vez vê os botões no primeiro quadro.
//
// Nada aqui é segredo nem é confiado: são as chaves públicas do Firebase e
// três booleanos sobre o que o servidor oferece. A cópia local só decide o
// que desenhar; quem entra é sempre o servidor que decide.

import type { AuthConfig } from './api';

const KEY = 'wa.authconfig';

/**
 * A cópia guardada pinta; quem decide entrar é a resposta do servidor.
 *
 * Medido da pior maneira: com esta cópia a chegar já no primeiro render,
 * o login no iPhone deixou de funcionar por completo. `cfg` era ao mesmo
 * tempo "o que desenhar" e "está tudo pronto para começar", e o
 * redirecionamento passou a partir antes de a página assentar em vez de
 * uns 100 ms depois. As duas coisas estão separadas agora — ver
 * `configIsFresh` em App.tsx — e é isso que mantém isto seguro.
 */
const USE_CACHE: boolean = true;

export function rememberedConfig(): AuthConfig | null {
  if (!USE_CACHE) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw) as AuthConfig;
    // Uma cópia de um formato antigo é pior do que nenhuma.
    return typeof cfg?.passkeysAvailable === 'boolean' ? cfg : null;
  } catch { return null; }
}

export function keepConfig(cfg: AuthConfig): void {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch { /* private mode */ }
}
